import { useState, useRef, useCallback, useEffect } from 'react';
import { useAudioCapture } from './useAudioCapture';
import { encodeAudioForAPI } from '@/utils/audioProcessor';
import { AudioQueue } from '@/utils/audioQueue';
import { persistVoiceSession, type PersistVoiceSessionResult } from '@/utils/persistVoiceSession';
import { createOnceAsync } from '@/utils/onceAsync';
import { TARGET_SAMPLE_RATE } from '@/utils/pcm';
import { applyAssistantDelta, applyUserDelta, parseVoiceEvent } from '@/utils/voiceEvents';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { buildRealtimeVoiceWsUrl } from '@/integrations/supabase/project';
import type { CatalogSituation } from '@/lib/catalog';
import { voiceSceneContext } from '@/lib/sceneCast';

export type VoiceAgentStatus = 'idle' | 'connecting' | 'listening' | 'speaking';

interface Transcript {
  role: 'user' | 'assistant';
  text: string;
  timestamp: Date;
}

interface UseVoiceAgentProps {
  catalogSituation?: CatalogSituation | null;
  onSessionPersisted?: (result: PersistVoiceSessionResult) => void;
}

const SPEAKING_IDLE_MS = 800;

const EMPTY_PERSIST: PersistVoiceSessionResult = { sessionId: null, tooShort: true, userTurns: 0 };

export const useVoiceAgent = ({ catalogSituation, onSessionPersisted }: UseVoiceAgentProps = {}) => {
  const [status, setStatus] = useState<VoiceAgentStatus>('idle');
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  const audioQueueRef = useRef<AudioQueue | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const transcriptsRef = useRef<Transcript[]>([]);
  const sessionStartedAtRef = useRef<number | null>(null);
  const persistOnceRef = useRef(createOnceAsync<PersistVoiceSessionResult>());
  const notifiedPersistRef = useRef(false);
  const catalogRef = useRef(catalogSituation);
  const onPersistedRef = useRef(onSessionPersisted);
  const speakingTimerRef = useRef<number | null>(null);
  const { startCapture, stopCapture } = useAudioCapture();
  const { toast } = useToast();

  catalogRef.current = catalogSituation;
  onPersistedRef.current = onSessionPersisted;

  useEffect(() => {
    transcriptsRef.current = transcripts;
  }, [transcripts]);

  const persistIfNeeded = useCallback(async (): Promise<PersistVoiceSessionResult> => {
    if (!sessionStartedAtRef.current && !persistOnceRef.current.pending) {
      return EMPTY_PERSIST;
    }

    const result = await persistOnceRef.current.run(async () => {
      const startedAt = sessionStartedAtRef.current;
      if (!startedAt) return EMPTY_PERSIST;
      sessionStartedAtRef.current = null;
      try {
        return await persistVoiceSession({
          catalogSlug: catalogRef.current?.slug,
          startedAt,
          transcripts: transcriptsRef.current,
        });
      } catch (error) {
        console.error('Error persisting voice session:', error);
        return EMPTY_PERSIST;
      }
    });

    if (result.sessionId && !notifiedPersistRef.current) {
      notifiedPersistRef.current = true;
      onPersistedRef.current?.(result);
    }
    return result;
  }, []);

  const markListeningSoon = useCallback(() => {
    if (speakingTimerRef.current) {
      window.clearTimeout(speakingTimerRef.current);
    }
    speakingTimerRef.current = window.setTimeout(() => {
      setStatus((current) => (current === 'speaking' ? 'listening' : current));
    }, SPEAKING_IDLE_MS);
  }, []);

  const connect = useCallback(async () => {
    try {
      setStatus('connecting');
      setTranscripts([]);
      transcriptsRef.current = [];
      persistOnceRef.current.reset();
      notifiedPersistRef.current = false;
      sessionStartedAtRef.current = null;

      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE });
        audioQueueRef.current = null;
      }
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }
      if (!audioQueueRef.current) {
        audioQueueRef.current = new AudioQueue(audioContextRef.current);
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Sessão expirada. Entre novamente para praticar.');
      }

      const scene = catalogSituation;
      if (!scene?.title) {
        throw new Error('Escolha uma situação para praticar.');
      }

      const context = voiceSceneContext(scene);

      const wsUrl = buildRealtimeVoiceWsUrl(session.access_token, {
        url: import.meta.env.VITE_SUPABASE_URL,
        publishableKey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      });
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      let opened = false;
      let captureStarted = false;

      const startMic = async () => {
        if (captureStarted) return;
        captureStarted = true;
        sessionStartedAtRef.current = Date.now();
        persistOnceRef.current.reset();
        notifiedPersistRef.current = false;
        setStatus('listening');
        try {
          await startCapture((audioData) => {
            if (ws.readyState === WebSocket.OPEN) {
              const encoded = encodeAudioForAPI(audioData);
              ws.send(JSON.stringify({
                type: 'session.input_audio.append',
                audio: encoded,
              }));
            }
          }, audioContextRef.current ?? undefined);
        } catch (error) {
          const denied = error instanceof DOMException && error.name === 'NotAllowedError';
          toast({
            title: denied ? 'Microfone bloqueado' : 'Erro no microfone',
            description: denied
              ? 'Permita o microfone no navegador para praticar a conversa.'
              : error instanceof Error ? error.message : 'Não foi possível capturar o áudio',
            variant: 'destructive',
          });
          ws.close();
        }
      };

      ws.onopen = () => {
        opened = true;
        ws.send(JSON.stringify({
          type: 'hellopeople.context',
          context,
        }));
      };

      ws.onmessage = async (event) => {
        try {
          const data: unknown = JSON.parse(event.data);
          const parsed = parseVoiceEvent(data);

          switch (parsed.kind) {
            case 'session_ready':
              await startMic();
              break;

            case 'audio_delta':
              setStatus('speaking');
              await audioQueueRef.current?.addToQueue(parsed.pcm);
              markListeningSoon();
              break;

            case 'user_transcript':
              setTranscripts((prev) => applyUserDelta(prev, parsed.text));
              break;

            case 'assistant_delta':
              setTranscripts((prev) => applyAssistantDelta(prev, parsed.text));
              break;

            case 'error':
              toast({
                title: parsed.reason === 'payment_required'
                  ? 'Assine para ensaiar'
                  : parsed.reason === 'limit_reached'
                    ? 'Assine para ensaiar'
                    : 'Erro de Conexão',
                description: parsed.error || 'Erro ao conectar com o servidor',
                variant: 'destructive',
              });
              break;
          }
        } catch (error) {
          console.error('Error processing message:', error);
        }
      };

      ws.onerror = () => {
        if (opened) {
          toast({
            title: 'Erro de Conexão',
            description: 'Não foi possível estabelecer conexão',
            variant: 'destructive',
          });
        }
        setStatus('idle');
      };

      ws.onclose = () => {
        if (!opened) {
          toast({
            title: 'Conexão recusada',
            description: 'Sessão inválida ou assinatura necessária para ensaiar.',
            variant: 'destructive',
          });
        } else {
          void persistIfNeeded();
        }
        setStatus('idle');
        try {
          stopCapture();
          audioQueueRef.current?.clear();
        } catch (error) {
          console.error('Error tearing down voice capture:', error);
        }
      };

    } catch (error) {
      console.error('Error connecting:', error);
      toast({
        title: "Erro",
        description: error instanceof Error ? error.message : "Erro ao conectar",
        variant: "destructive"
      });
      setStatus('idle');
    }
  }, [catalogSituation, startCapture, stopCapture, persistIfNeeded, toast, markListeningSoon]);

  const teardownCapture = useCallback(() => {
    try {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      stopCapture();
      audioQueueRef.current?.clear();
    } catch (error) {
      console.error('Error tearing down voice session:', error);
    }
    setStatus('idle');
  }, [stopCapture]);

  const disconnect = useCallback(() => {
    void persistIfNeeded();
    teardownCapture();
  }, [persistIfNeeded, teardownCapture]);

  const endSession = useCallback(async (): Promise<string | null> => {
    let sessionId: string | null = null;
    try {
      sessionId = (await persistIfNeeded()).sessionId;
    } catch (error) {
      console.error('Error ending voice session:', error);
    }
    teardownCapture();
    return sessionId;
  }, [persistIfNeeded, teardownCapture]);

  const interrupt = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'hellopeople.interrupt',
      }));
    }
    audioQueueRef.current?.clear();
    setStatus('listening');
  }, []);

  useEffect(() => {
    return () => {
      if (speakingTimerRef.current) {
        window.clearTimeout(speakingTimerRef.current);
      }
      disconnect();
      audioContextRef.current?.close();
    };
  }, [disconnect]);

  return {
    status,
    transcripts,
    connect,
    disconnect,
    endSession,
    interrupt
  };
};
